import React, {useEffect} from 'react';
import {useBlocker} from 'react-router-dom';
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from '@mui/material';

/*
 * Android's leave guard (the Job Pricing and Automatic follow-ups pages):
 * while a page has unsaved edits, leaving it by any route asks "Leave without
 * saving?" first.
 *
 * - Inside the app (the menu, the menu's foot, a link, the browser's Back):
 *   the router holds the move and this dialog asks; Discard changes goes on,
 *   Keep editing stays.
 * - Closing or reloading the tab: the browser's own question (a page cannot
 *   draw its own dialog there).
 *
 * `const leaveGuard = useLeaveGuard(dirty, 'Your changes have not been saved.', onDiscard);`
 * and render `{leaveGuard}` anywhere in the page.
 */
export const useLeaveGuard = (dirty: boolean, message: string, onDiscard?: () => void) => {
  const blocker = useBlocker(({currentLocation, nextLocation}) =>
    dirty && (currentLocation.pathname !== nextLocation.pathname || currentLocation.search !== nextLocation.search));

  useEffect(() => {
    if (!dirty) return undefined;
    const ask = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', ask);
    return () => window.removeEventListener('beforeunload', ask);
  }, [dirty]);

  // Saved (or discarded) while a move was held: nothing left to ask about.
  useEffect(() => {
    if (blocker.state === 'blocked' && !dirty) blocker.proceed();
  }, [blocker, dirty]);

  return (
    <Dialog open={blocker.state === 'blocked'} onClose={() => blocker.state === 'blocked' && blocker.reset()}
            aria-labelledby="leave-guard-title">
      <DialogTitle id="leave-guard-title">Leave without saving?</DialogTitle>
      <DialogContent><DialogContentText>{message}</DialogContentText></DialogContent>
      <DialogActions>
        <Button onClick={() => blocker.state === 'blocked' && blocker.reset()}>Keep editing</Button>
        <Button color="error" onClick={() => {
          onDiscard?.();
          if (blocker.state === 'blocked') blocker.proceed();
        }}>Discard changes</Button>
      </DialogActions>
    </Dialog>
  );
};
